/**
 * Screen wake lock, held for exactly as long as a voice recording lasts.
 *
 * Wraps `@capacitor-community/keep-awake`, which sets
 * `UIApplication.shared.isIdleTimerDisabled` on iOS, adds `FLAG_KEEP_SCREEN_ON`
 * to the activity window on Android, and calls `navigator.wakeLock.request`
 * on the web. Nothing here needs a new permission: the Android flag is a
 * window flag, not the WAKE_LOCK permission.
 *
 * Two rules carry over from useHaptics.js and are the reason this file exists
 * rather than two inline calls:
 *
 *   1. The Capacitor plugin proxy is "thenable" — it intercepts every property
 *      access, so returning it from an `async` function makes promise
 *      resolution invoke the proxy's `.then`, which the native bridge rejects
 *      with "<Plugin>.then() is not implemented on ios". So the load stays
 *      async, the accessor is synchronous, and no function below ever returns
 *      the plugin object.
 *   2. Keep-awake is strictly best-effort. `isSupported()` is consulted first,
 *      every native call is inside try/catch, and no failure may block, delay
 *      or fail a recording. A learner on a browser without `navigator.wakeLock`
 *      or on a build where the plugin somehow is not registered gets today's
 *      behaviour and nothing worse.
 *
 * The contract is a desired-state reconciler, not a pair of fire-and-forget
 * calls, and its state lives at module scope. Both parts matter:
 *
 *   - The native lock is a single global boolean on both platforms, so there is
 *     no reference count that a stale release could decrement harmlessly. A
 *     generation counter does not close the hole either: recording A's acquire
 *     is in flight, the learner cancels, starts recording B, B's acquire
 *     resolves and sets the flag, then A's acquire resolves late and calls
 *     `allowSleep()`, clearing the flag B relies on. Here there is one `wanted`
 *     boolean and one promise chain; each queued step reads `wanted` when its
 *     turn comes rather than replaying the intent it was created with, so a
 *     late-resolving step can only ever move the native flag toward the current
 *     desired state.
 *   - Module scope, because AudioRecorder is unmounted and remounted on every
 *     navigation (DashboardView keys the RouterView by route path with no
 *     keep-alive). With per-instance state an unmounted recorder's queued
 *     release could still clear a newly mounted recorder's lock.
 *
 * Serializing the native calls does mean a recording that starts while the
 * previous recording's `allowSleep()` is still in flight waits for that call to
 * land before its own `keepAwake()` is issued. That ordering is deliberate:
 * letting the two overlap is exactly the race described above. What is not
 * acceptable is a bridge call misbehaving in a way that leaves the screen
 * pinned on with nothing queued to correct it, and there are two such ways. A
 * call can never settle, which wedges the queue and with it every later
 * release; `bounded` plus the delayed re-issue handle that. Or a call can
 * settle long after we stopped waiting for it, moving the OS behind an empty
 * queue; the abandoned-call handler in `reconcile` handles that.
 */

// Long enough that a slow-but-working bridge is never cut off, short enough
// that a wedged one cannot hold the queue for the length of a recording.
const NATIVE_CALL_TIMEOUT_MS = 5000;

// How long after a timeout to re-issue the desired state. This is the recovery
// for a call that never settles at all, because nothing else will ever run.
const TIMEOUT_RETRY_DELAY_MS = 1000;

// Recovery attempts are bounded so a permanently misbehaving bridge cannot spin
// forever. Reset by every call that does settle in time.
const RECOVERY_LIMIT = 3;

let keepAwakeModule = null;
let loadPromise = null;
let supportPromise = null;

// The desired state, and the last state we successfully pushed to the OS.
// `applied` is null when the OS state is UNKNOWN, which is what a timed-out or
// failed call leaves behind.
let wanted = false;
let applied = false;
let recoveriesLeft = RECOVERY_LIMIT;

// A call we stopped waiting for that is still running. Only ever set for a
// timed-out call, and cleared the moment it settles. See the acquire guard in
// `reconcile`.
let abandonedCall = null;

// Every acquire/release appends one step here, so the native calls are
// serialized and can never interleave.
let chain = Promise.resolve();

/**
 * Stop waiting on a bridge call after NATIVE_CALL_TIMEOUT_MS. The call itself
 * keeps running; only our wait on it ends.
 * @param {Promise<any>} call
 */
function bounded(call) {
  return new Promise((resolve, reject) => {
    let settled = false;
    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      const error = new Error("keep-awake native call timed out");
      // Distinguished from an ordinary rejection because only a timeout leaves
      // a call that may still land later. An outright rejection is a settled
      // answer, and re-running it would just repeat the same failure.
      error.isKeepAwakeTimeout = true;
      reject(error);
    }, NATIVE_CALL_TIMEOUT_MS);
    const finish = (fn) => (value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      fn(value);
    };
    call.then(finish(resolve), finish(reject));
  });
}

/**
 * Push the current desired state to the OS again, ignoring anything we believe
 * about it. Recovery only: after a timeout the cached belief is worthless,
 * because the abandoned call may land at any moment, and both native calls are
 * idempotent so a redundant re-issue costs nothing.
 */
function forceReconcile() {
  if (recoveriesLeft <= 0) return;
  recoveriesLeft -= 1;
  applied = null;
  reconcile().catch(() => {});
}

async function ensureLoaded() {
  if (keepAwakeModule) return;
  if (!loadPromise) {
    loadPromise = import("@capacitor-community/keep-awake")
      .then((mod) => {
        keepAwakeModule = mod;
      })
      .catch(() => {});
  }
  await loadPromise;
}

// Synchronous on purpose — see rule 1 in the header.
const getPlugin = () => keepAwakeModule?.KeepAwake ?? null;

/**
 * Whether the OS/browser can hold a wake lock at all. Memoized on the promise:
 * the answer is a property of the platform, and asking once avoids a bridge
 * round trip on every reconcile step.
 * @returns {Promise<boolean>}
 */
async function ensureSupported() {
  await ensureLoaded();
  const plugin = getPlugin();
  if (!plugin) return false;
  if (!supportPromise) {
    supportPromise = bounded(Promise.resolve().then(() => plugin.isSupported()))
      .then((result) => result?.isSupported === true)
      .catch(() => {
        // A DEFINITIVE false is worth caching; a failed or timed-out check is
        // not. One slow bridge call during a cold start would otherwise disable
        // keep-awake for the rest of the session.
        supportPromise = null;
        return false;
      });
  }
  return supportPromise;
}

/**
 * Queue one step that pushes the current desired state to the OS.
 * @returns {Promise<"acquired"|"released"|"unsupported"|"failed">} what the
 *   step observed.
 */
function reconcile() {
  const step = chain.then(async () => {
    if (!(await ensureSupported())) return "unsupported";

    // Read at OUR turn, and as late as possible — after the support check, not
    // when this step was created. This is the whole point of the design: a step
    // created by a cancelled recording's release that runs after a new
    // recording's acquire must not undo the new lock.
    const desired = wanted;
    if (applied !== null && desired === applied) {
      return desired ? "acquired" : "released";
    }

    // An acquire must never run alongside an earlier acquire we abandoned.
    // KeepAwakeWeb.keepAwake() assigns the WakeLockSentinel it gets into a
    // single slot, so two concurrent requests both write it, the loser is
    // orphaned with no reference left, and allowSleep() can only ever release
    // the winner — an unreleasable wake lock. (A SEQUENTIAL second acquire is
    // safe: that method releases the stored sentinel before requesting a new
    // one. Concurrency is the whole problem.) Doing nothing here is not
    // giving up on the lock: when the abandoned call settles it reconciles
    // against whatever is wanted by then, which picks this acquire up.
    if (desired && abandonedCall) {
      console.warn("[keep-awake] acquire deferred behind an abandoned call");
      return "failed";
    }

    const plugin = getPlugin();
    const call = Promise.resolve().then(() =>
      desired ? plugin.keepAwake() : plugin.allowSleep()
    );
    try {
      await bounded(call);
      applied = desired;
      recoveriesLeft = RECOVERY_LIMIT;
      return desired ? "acquired" : "released";
    } catch (err) {
      applied = null;
      console.warn("[keep-awake] native call failed:", err);
      if (err?.isKeepAwakeTimeout) {
        // Two recoveries, because a timeout covers two different failures and
        // there is no way to tell them apart while it is happening.
        //
        // One: the call never settles at all, so nothing else will ever run
        // and the OS is left wherever it was. Re-issue after a short delay —
        // but ONLY for a release. Re-issuing an ACQUIRE would run a second
        // keepAwake() while the abandoned one is still in flight, and the
        // plugin's web implementation keeps a single WakeLockSentinel:
        // whichever request resolves last overwrites the other handle, so one
        // sentinel is orphaned with nothing able to release it, which is the
        // exact outcome this recovery exists to prevent. A release carries no
        // such hazard (allowSleep takes no handle and is idempotent), and an
        // unapplied release is the direction that pins the screen on. An
        // acquire that never lands merely means no lock, which is what
        // best-effort already allows.
        if (!desired) setTimeout(forceReconcile, TIMEOUT_RETRY_DELAY_MS);
        // Two: it settles long after we stopped waiting, having moved the OS
        // behind an empty queue — an acquire landing after its own release
        // already went through leaves the screen pinned on for good. This
        // handler runs once the call is genuinely done, so it never overlaps
        // it, and it reconciles against whatever is wanted by then.
        abandonedCall = call;
        const onAbandonedSettled = () => {
          if (abandonedCall === call) abandonedCall = null;
          forceReconcile();
        };
        call.then(onAbandonedSettled, onAbandonedSettled);
      }
      return "failed";
    }
  });

  // The chain must survive a rejecting step, or one failure would wedge every
  // later acquire and release. The step above cannot reject, but a swallowed
  // rejection here costs nothing and makes that guarantee local.
  chain = step.then(
    () => undefined,
    () => undefined
  );
  return step;
}

/**
 * A step performs whatever the CURRENT desired state is, which for an acquire
 * whose recording already ended is a release. That is correct behaviour, but it
 * is not an outcome the caller asked about, so it is reported as "superseded"
 * and the caller skips its telemetry rather than reporting an outcome outside
 * the event's declared set.
 */
function forIntent(intent, outcome) {
  if (intent && outcome === "released") return "superseded";
  if (!intent && outcome === "acquired") return "superseded";
  return outcome;
}

export function useKeepAwake() {
  return {
    /**
     * Ask the OS to keep the screen on. Never throws.
     * @returns {Promise<"acquired"|"superseded"|"unsupported"|"failed">}
     */
    acquire() {
      wanted = true;
      return reconcile().then((outcome) => forIntent(true, outcome));
    },

    /**
     * Let the screen dim again. Never throws. Callers release unconditionally,
     * without consulting the feature flag: the flag can flip mid-recording,
     * and on Android a flag left set is not cleared when the activity
     * backgrounds — the OS just stops honouring it until the activity is in
     * front again, so a missed release would pin the screen on the next
     * resume.
     * @returns {Promise<"released"|"superseded"|"unsupported"|"failed">}
     */
    release() {
      wanted = false;
      return reconcile().then((outcome) => forIntent(false, outcome));
    },
  };
}
